import { Button, Divider, Typography } from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import { ReorderableBlockList } from '../../../CommonComponents/ReorderableBlockList';
import { IStory } from '@opengov/stories-shared/types';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import {
  useCurrentSubjectClaims,
  useSubjectPublicationStoryClaim
} from '../../../../hooks/subjectClaims';
import { STORIES_ADMIN_PRIVILEGE } from '@opengov/stories-shared/constants';
import {
  IOrganizeStoriesGraphQlRequest,
  IOrganizeStorySectionsGraphQlRequest,
  IPublicationViewMessages,
  ISessionUser,
  IStoriesFolder,
  IStoryListMessages
} from '../../../../../types';
import {
  useOrganizeStoriesInFolder,
  useOrganizeStorySectionsById,
  useUpdatePublicationUpdatedAt
} from '../../PublicationView/hooks/usePublicationMutations';
import { usePublicationById } from '../../PublicationView/hooks/usePublicationById';
import { usePublicationFolderStructure } from '../../PublicationView/hooks/usePublicationFolderStructure';
import { usePublicationStories } from '../../PublicationView/hooks/usePublicationStories';
import { navigateToOrganizeSectionAndStoriesPath } from '../../../../state/navigation';

import { NavigationBlockerPopup } from '../../../Organize/NavigationBlockerPopup';

import { FolderSvg } from '../../../StoryList/StoryListContent/FolderSvg';

import { Sidebar } from '../../../StoryList/Sidebar';

// import { NavigationBlockerPopup } from '../NavigationBlockerPopup';
import { OrganizeFoldersModal } from '../../../Organize/OrganizeFoldersModal';
import { PageHeader } from '../../../Organize/PageHeader';
import { StoryListTable } from '../../../Organize/StoryListTable';

import styles from './OrganizeSectionsAndStories.scss';
import { PageErrorBoundary } from '../../../ErrorBoundary/PageErrorBoundary';
import { ViewName } from '../../../../../instrumentation/views';

export interface IOrganizeSectionsAndStoriesProps {
  publicationId: string;
  selectedSectionId: string;
  rootSectionId: string;
  entityId: number;
  entityUuid: string;
  user: ISessionUser;
  messages: IPublicationViewMessages;
  storyMessages: IStoryListMessages;
  entitySubdomain: string;
  storiesAccessControlEnabled: boolean;
  navigateToPublicationSectionList(publicationId: string, sectionId: string): void;
  navigateToPublicationStoriesList(publicationId: string): void;
}

export const getSelectedSectionStructure = (allSections: IStoriesFolder[], sectionId: string): IStoriesFolder[] => {
  const allStoriesSections = _.cloneDeep(allSections);
  const selectedSectionStructure = [];
  function searchSection(sections, selectedSectionID) {
    sections?.forEach((section) => {
      if (section.id === selectedSectionID) {
        selectedSectionStructure.push(section);
      } else if (section?.children?.length) {
        searchSection(section.children, selectedSectionID);
      }
    });
  }
  searchSection(allStoriesSections, sectionId);
  return selectedSectionStructure;
};

const OrganizeSectionsAndStoriesInternal: React.FC<IOrganizeSectionsAndStoriesProps> = (props) => {
  const {
    publicationId,
    messages,
    entitySubdomain,
    rootSectionId,
    storyMessages,
    selectedSectionId,
    entityId,
    entityUuid,
    user,
    storiesAccessControlEnabled,
    navigateToPublicationSectionList,
    navigateToPublicationStoriesList
  } = props;

  const { data: currentSubjectClaims } = useCurrentSubjectClaims(
    entityUuid && user?.uuid ? { userUuid: user.uuid, entityUuid } : null
  );
  const { data: publicationStoryClaimResult } = useSubjectPublicationStoryClaim(
    publicationId && user?.uuid && entityUuid
      ? {
        publicationId,
        subjectId: user.uuid,
        entityUuid,
        storiesAccessControlEnabled
      }
      : null
  );
  const currentSubjectPublicationStoryClaim = publicationStoryClaimResult?.allClaims ?? null;
  const sectionIds = publicationStoryClaimResult?.sectionIds ?? [];
  const isAdmin =
    Array.isArray(currentSubjectPublicationStoryClaim) &&
    currentSubjectPublicationStoryClaim.includes(STORIES_ADMIN_PRIVILEGE);

  // Fetch publication, folders, and stories via hooks.
  const { data: publication } = usePublicationById(
    publicationId && entityId > 0 ? { publicationId } : null,
    storyMessages.somethingWentWrongError
  );

  const { publicationFolderStructure = [] } = usePublicationFolderStructure(
    publicationId && entityId > 0 ? { publicationId, entityId, sectionIds } : null,
    storyMessages.somethingWentWrongError
  );

  const [selectedSection, setSelectedSection] = useState<IStoriesFolder>(null);

  const storiesQueryParams = useMemo(
    () =>
      publicationId && entityId > 0 && selectedSection?.id
        ? {
          publicationId,
          entityId,
          sectionId: selectedSection.id,
          page: 1,
          storyState: 'all' as const,
          showAllStoriesForSelectedFolder: true,
          order: 'asc' as const,
          userUuid: user?.uuid ?? '',
          entityUuid,
          storiesAccessControlEnabled,
          isAdmin
        }
        : null,
    [publicationId, entityId, selectedSection?.id, user?.uuid, entityUuid, storiesAccessControlEnabled, isAdmin]
  );

  const {
    data: stories,
    isLoading: isStoriesLoading,
    refetch: getStories
  } = usePublicationStories(storiesQueryParams, storyMessages.somethingWentWrongError);

  const currentSectionName = selectedSection?.displayName ?? '';

  // Section-reorder + updatedAt writes via hooks instead of the retired
  // compose-app effects. The local `organizeStorySectionsByOrder`
  // wrapper preserves the retired prop's signature/behavior (errors swallowed,
  // toasts via the hook) so OrganizeFoldersModal keeps its callback contract.
  const updateTimestampMutation = useUpdatePublicationUpdatedAt();
  const organizeStorySectionsMutation = useOrganizeStorySectionsById(
    { title: storyMessages.changesSaved, body: storyMessages.changesSavedSuccessfully },
    { title: storyMessages.somethingWentWrongError, body: storyMessages.storyOrganizeFoldersFailed }
  );
  const organizeStorySectionsByOrder = async (req: IOrganizeStorySectionsGraphQlRequest): Promise<void> => {
    try {
      await organizeStorySectionsMutation.mutateAsync({
        orderedSectionsIds: req.orderedSectionsIds,
        entityId: req.entityId,
        publicationId: req.publicationId
      });
      updateTimestampMutation.mutate({ publicationId: req.publicationId, userId: req.userId });
    } catch {
      // Error toast emitted by useOrganizeStorySectionsById.
    }
  };

  // Replaces the retired storyListEffects.organizeStoriesById prop. Errors are
  // swallowed so the caller's post-save cleanup (.then) always runs, matching
  // the old effect. Toasts emitted by useOrganizeStoriesInFolder.
  const organizeStoriesInFolderMutation = useOrganizeStoriesInFolder(
    { title: storyMessages.changesSaved, body: storyMessages.changesSavedSuccessfully },
    { title: storyMessages.somethingWentWrongError, body: storyMessages.storyOrganizeFoldersFailed }
  );
  const organizeStoriesByOrder = async (req: IOrganizeStoriesGraphQlRequest): Promise<void> => {
    try {
      await organizeStoriesInFolderMutation.mutateAsync(req);
    } catch {
      // Error toast emitted by useOrganizeStoriesInFolder.
    }
  };

  const rootId = rootSectionId;
  const selectedId = publicationFolderStructure[0]?.id;
  const selectedSectionStructure = publicationFolderStructure;
  const navigateToSection = () => {
    if (publication?.id) {
      if (selectedSectionId) {
        navigateToPublicationSectionList(publication?.id, selectedSectionId);
      } else {
        navigateToPublicationStoriesList(publication.id);
      }
    }
  };

  const [isOrganizeStoriesInProgress, setIsOrganizeStoriesInProgress] = useState<boolean>(false);
  const [organizedStoriesList, setOrganizedStoriesList] = useState<IStory[]>([]);
  const [savingOrganizeStories, setSavingOrganizeStories] = useState<boolean>(false);
  const [isOrganizeSectionModalOpen, setIsOrganizeSectionModalOpen] = useState(false);

  const [isReorderRootSectionsActive, setIsReorderRootSectionsActive] = useState(false);
  const [reorderableRootSections, setReorderableRootSections] = useState(publicationFolderStructure);
  //If sections are reorderd
  const [isRootSectionsReorderInProgress, setIsRootSectionsReorderInProgress] = useState(false);
  const [showBlockerPopup, setShowBlockerPopup] = useState(false);
  const [navLink, setNavLink] = useState(null);
  const [clickedFolder, setClickedFolder] = useState(null);

  const getPublicationStories = () => {
    if (storiesQueryParams) {
      void getStories();
    }
  };

  const handleNavigationClick = (event) => {
    if (!isOrganizeStoriesInProgress || !isRootSectionsReorderInProgress) return;
    let targetLink;
    if (event.target.tagName === 'A') {
      targetLink = event.target.href;
    } else {
      targetLink = event.target.closest('a')?.href;
    }
    event.preventDefault();
    setNavLink(targetLink);
    setShowBlockerPopup(true);
  };

  const handleBackClick = () => {
    setIsOrganizeStoriesInProgress(false);
    setIsRootSectionsReorderInProgress(false);
    setIsReorderRootSectionsActive(false);
    const selectedSecId = location.href.split('/').pop();
    const selected = getSelectedSectionStructure(publicationFolderStructure, selectedSecId);
    if (selected[0] && !selectedSection) {
      setSelectedSection(selected[0]);
    }
  };

  const handleBrowserNavigation = (event) => {
    if ((isOrganizeStoriesInProgress || isRootSectionsReorderInProgress) && !navLink && !showBlockerPopup) {
      event.preventDefault();
      event.returnValue = storyMessages.unsavedChangesDescription;
      return '';
    }
  };

  useEffect(() => {
    if (!showBlockerPopup && !isOrganizeStoriesInProgress) getPublicationStories();
    const selected = getSelectedSectionStructure(publicationFolderStructure, selectedId);
    if (selected[0] && !selectedSection) {
      setSelectedSection(selected[0]);
    }

    window.addEventListener('beforeunload', handleBrowserNavigation);
    window.document.querySelector('nav')?.addEventListener('click', handleNavigationClick);
    window.addEventListener('popstate', handleBackClick);

    return () => {
      window.removeEventListener('popstate', handleBackClick);
      window.removeEventListener('beforeunload', handleBrowserNavigation);
      window.document.querySelector('nav')?.removeEventListener('click', handleNavigationClick);
    };
  }, [
    entityId,
    selectedSectionStructure,
    isOrganizeStoriesInProgress,
    isRootSectionsReorderInProgress,
    showBlockerPopup
  ]);

  useEffect(() => {
    if (publicationFolderStructure && publicationFolderStructure.length && !reorderableRootSections?.length) {
      setReorderableRootSections(publicationFolderStructure);
    }
  }, [publicationFolderStructure, isReorderRootSectionsActive]);

  useEffect(() => {
    getPublicationStories();
  }, [selectedSection?.id]);

  const setSectionSelection = (section: IStoriesFolder) => {
    setClickedFolder(section);
    if (isOrganizeStoriesInProgress) {
      setShowBlockerPopup(true);
      setSelectedSection(null);
      return;
    }
    setSelectedSection(getSelectedSectionStructure(selectedSectionStructure, section.id)[0] || null);
    navigateToOrganizeSectionAndStoriesPath(entitySubdomain, publication?.id, rootId, section.id);
  };

  const hasSubSections = () => selectedSection?.children?.length > 1;

  if (selectedSectionStructure.length) {
    selectedSectionStructure[0].toggled = true;
  }

  const cancelStoriesButtonAction = () => {
    setIsOrganizeStoriesInProgress(false);
  };

  const saveStoriesButtonAction = () => {
    if (organizedStoriesList?.length) {
      setSavingOrganizeStories(true);
      const orderedStoriesIds = organizedStoriesList.map((childNode) => childNode._id);
      void organizeStoriesByOrder({
        orderedStoriesIds,
        entityId,
        selectedFolderId: selectedSectionId,
        currentFolderName: currentSectionName
      }).then(() => {
        updateTimestampMutation.mutate({ publicationId: publication.id, userId: user.id });
        setSavingOrganizeStories(false);
        setIsOrganizeStoriesInProgress(false);
        getPublicationStories();
      });
    } else {
      setSavingOrganizeStories(false);
    }
  };

  const saveSectionsButtonAction = () => {
    const orderedSectionsIds = reorderableRootSections.map((section) => section.id);
    void organizeStorySectionsByOrder({
      orderedSectionsIds,
      entityId,
      currentSectionName,
      publicationId: publication?.id,
      userId: user.id
    }).then(() => {
      setIsRootSectionsReorderInProgress(false);
      setIsReorderRootSectionsActive(false);
      setReorderableRootSections(null);
    });
  };

  const cancelSectionsButtonAction = () => {
    setIsRootSectionsReorderInProgress(false);
    setIsReorderRootSectionsActive(false);
    setReorderableRootSections(null);
  };

  const navigateToNewFolder = () => {
    if (!!navLink) {
      const link = navLink;
      setNavLink(null);
      window.location.href = link;
      return;
    }

    setSelectedSection(getSelectedSectionStructure(selectedSectionStructure, clickedFolder.id)[0] || null);
    navigateToOrganizeSectionAndStoriesPath(entitySubdomain, publication?.id, rootId, clickedFolder.id);
    setIsOrganizeStoriesInProgress(false);
    setShowBlockerPopup(false);
  };

  const setPreviousSelectedFolder = () => {
    navigateToOrganizeSectionAndStoriesPath(entitySubdomain, publication?.id, rootId, selectedSection.id);
    setNavLink(null);
    setShowBlockerPopup(false);
  };

  const renderModal = () => {
    const organizeFoldersModal =
      isOrganizeSectionModalOpen &&
      createPortal(
        <OrganizeFoldersModal
          isOrganizePublicationSections={true}
          messages={storyMessages}
          selectedFolderId={selectedSection?.id}
          folderStructure={publicationFolderStructure}
          entityId={entityId}
          currentFolderName={selectedSection?.id}
          publicationId={publication?.id}
          userId={user?.id}
          organizeStorySectionsByOrder={organizeStorySectionsByOrder}
          onClose={() => setIsOrganizeSectionModalOpen(false)}
        />,
        document.getElementById('app')
      );

    const navigationBlockerModal = showBlockerPopup && (
      <NavigationBlockerPopup
        messages={storyMessages}
        onConfirmNavigate={navigateToNewFolder}
        onCancel={setPreviousSelectedFolder}
      />
    );
    return [organizeFoldersModal, navigationBlockerModal];
  };

  const onSectionDragEnd = (sourceIndex: number, destinationIndex: number) => {
    const updatedChildNodes = [...reorderableRootSections];
    const reorderedNode = updatedChildNodes.splice(sourceIndex, 1)[0];
    updatedChildNodes.splice(destinationIndex, 0, reorderedNode);
    setReorderableRootSections(updatedChildNodes);
    setIsRootSectionsReorderInProgress(true);
  };

  const renderSectionReorderContentMessage = () => (
    <div className={styles.reoderContent}>
      <div className={styles.centered}>
        <FolderSvg />
        <h2>{messages.reorderSectionDescription}</h2>
      </div>
    </div>
  );

  return (
    <div className={styles.root} data-test="organize-sections-stories-page">
      {renderModal()}
      <PageHeader
        messages={storyMessages}
        isOrganizeStoriesInProgress={isOrganizeStoriesInProgress}
        isReorderRootSectionsActive={isReorderRootSectionsActive}
        entitySubdomain={entitySubdomain}
        folderId={rootSectionId}
        isOrganizeFromPublication={true}
        navigateToSection={navigateToSection}
      />
      <div className={styles.contentWrapper} data-test="organize-stories-sections-content">
        {!isReorderRootSectionsActive && (
          <Sidebar
            isOrganizeStories={true}
            isPublicationsTabSelected={true}
            publicationViewMessages={messages}
            currentFolderId={selectedSection?.id}
            entityId={entityId}
            folderStructure={selectedSectionStructure}
            isAdmin={isAdmin}
            messages={storyMessages}
            rootFolderId={rootId}
            entitySubdomain={entitySubdomain}
            isOrganizeSections={true}
            isOrganizeStoriesInProgress={isOrganizeStoriesInProgress}
            setSelectedSection={setSectionSelection}
            setIsReorderRootSections={setIsReorderRootSectionsActive}
            storiesAccessControlEnabled={storiesAccessControlEnabled}
            currentSubjectClaims={currentSubjectClaims}
            currentSubjectPublicationStoryClaim={currentSubjectPublicationStoryClaim}
          />
        )}
        {isReorderRootSectionsActive && (
          <div className={styles.reorderableList}>
            <div className={styles.filterRootSectionHeader}>
              <div className={styles.flexRow}>
                <FolderIcon aria-hidden={true} className={styles.folderIcon} />
                <Typography variant="h6" className={styles.foldersHeader}>
                  {messages.sections}
                </Typography>
              </div>
              {isReorderRootSectionsActive && (
                <div className={styles.buttons}>
                  <Button
                    data-test="organize-sections-cancel-button"
                    variant="outlined"
                    size="small"
                    onClick={cancelSectionsButtonAction}
                  >
                    {messages.cancel}
                  </Button>
                  <Button
                    disabled={!isRootSectionsReorderInProgress}
                    data-test="organize-sections-save-button"
                    variant="contained"
                    size="small"
                    onClick={saveSectionsButtonAction}
                  >
                    {messages.save}
                  </Button>
                </div>
              )}
            </div>
            <Divider />
            <ReorderableBlockList dataTest="reorderable-sections-list" onDragEnd={onSectionDragEnd}>
              {(reorderableRootSections || []).map((rootSection: IStoriesFolder) => (
                <ReorderableBlockList.Item
                  dataTest="reorderable-sections-list-item"
                  key={rootSection.id}
                  id={rootSection.id}
                  label={rootSection.displayName}
                />
              ))}
            </ReorderableBlockList>
          </div>
        )}
        {!isReorderRootSectionsActive ? (
          <div className={styles.content}>
            <div className={styles.contentHeader}>
              <Typography variant="h5" component="h2" data-test="selected-section-title">
                {selectedSection?.displayName}
              </Typography>
              <div className={styles.buttons}>
                {hasSubSections() && !!selectedSection && !!selectedSection?.id && (
                  <Button
                    disabled={isOrganizeStoriesInProgress}
                    data-test="organize-sections-button"
                    variant="outlined"
                    size="small"
                    onClick={() => setIsOrganizeSectionModalOpen(true)}
                  >
                    {messages.organizeSections}
                  </Button>
                )}
                {isOrganizeStoriesInProgress && (
                  <>
                    <Button
                      disabled={savingOrganizeStories}
                      data-test="organize-stories-cancel-button"
                      variant="outlined"
                      size="small"
                      onClick={cancelStoriesButtonAction}
                    >
                      {messages.cancel}
                    </Button>
                    <Button
                      disabled={savingOrganizeStories}
                      data-test="organize-stories-save-button"
                      variant="contained"
                      size="small"
                      onClick={saveStoriesButtonAction}
                    >
                      {messages.save}
                    </Button>
                  </>
                )}
              </div>
            </div>
            <div className={styles.description}>
              <Typography className={styles.organizeStoriesText}>
                {messages.organizeStories.toUpperCase()}
              </Typography>
              <Typography className={styles.organizeStoriesDescText}>
                {messages.organizeStoriesDescription}
              </Typography>
            </div>
            <Divider />
            <div className={styles.storiesTable}>
              <StoryListTable
                isOrganizePublicationStories={true}
                isLoading={isStoriesLoading}
                messages={storyMessages}
                storyListContents={stories}
                isOrganizeStoriesInProgress={isOrganizeStoriesInProgress}
                setIsOrganizeStoriesInProgress={setIsOrganizeStoriesInProgress}
                setOrganizedStoriesList={setOrganizedStoriesList}
              />
            </div>
          </div>
        ) : (
          renderSectionReorderContentMessage()
        )}
      </div>
    </div>
  );
};

export const OrganizeSectionsAndStories: React.FC<IOrganizeSectionsAndStoriesProps> = (props) => {
  return (
    <PageErrorBoundary viewName={ViewName.OrganizeSectionsAndStoriesPage}>
      <OrganizeSectionsAndStoriesInternal {...props} />
    </PageErrorBoundary>
  );
};
